import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  ArrowLeft,
  CalendarDays,
  ExternalLink,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";
import { db } from "../web/src/firebaseClient";

const attendanceOptions = [
  { value: "", label: "Seçilmedi" },
  { value: "present", label: "Katıldı" },
  { value: "absent", label: "Katılmadı" },
  { value: "late", label: "Geç Katıldı" },
  { value: "excused", label: "Mazeretli" },
];

const academicYears = Array.from({ length: 5 }, (_, index) => {
  const startYear = 2026 + index;
  return `${startYear}-${startYear + 1}`;
});

export default function MentorSessionsManagement() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [accessAllowed, setAccessAllowed] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentAppUser, setCurrentAppUser] = useState(null);

  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [selectedSessionId, setSelectedSessionId] = useState(null);

  const [form, setForm] = useState({
    title: "Mentör Görüşmesi",
    academicYear: academicYears[0],
    teamId: "",
    mentorIds: [],
    date: "",
    startTime: "",
    endTime: "",
    location: "",
    description: "",
    mentorNotes: "",
    attendance: {},
    status: "active",
  });

  const [message, setMessage] = useState({ type: "", text: "" });

  const [modal, setModal] = useState({
    open: false,
    type: "",
    title: "",
    text: "",
  });

  useEffect(() => {
    const init = async () => {
      const authUser = getAuth().currentUser;

      if (!authUser) {
        setAccessAllowed(false);
        setLoading(false);
        return;
      }

      const token = await authUser.getIdTokenResult();
      const admin = token.claims.role === "admin";

      const usersSnapshot = await getDocs(
        query(collection(db, "users"), orderBy("createdAt", "desc"))
      );

      const userList = usersSnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      const matchedUser = userList.find(
        (user) =>
          user.email?.toLowerCase() === authUser.email?.toLowerCase() ||
          user.uid === authUser.uid
      );

      const mentorAllowed =
        matchedUser?.allowed_roles?.mentor === true ||
        matchedUser?.allowedRoles?.mentor === true ||
        matchedUser?.roles?.mentor === true;

      setIsAdmin(admin);
      setCurrentAppUser(matchedUser || null);
      setAccessAllowed(admin || mentorAllowed);

      if (admin || mentorAllowed) {
        await fetchData(userList);
      }

      setLoading(false);
    };

    init();
  }, []);

  const fetchData = async (existingUsers = null) => {
    const userList =
      existingUsers ||
      (
        await getDocs(
          query(collection(db, "users"), orderBy("createdAt", "desc"))
        )
      ).docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

    const teamsSnapshot = await getDocs(
      query(collection(db, "teams"), orderBy("createdAt", "desc"))
    );

    const sessionsSnapshot = await getDocs(
      query(collection(db, "mentorSessions"), orderBy("createdAt", "desc"))
    );

    setUsers(userList);

    setTeams(
      teamsSnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }))
    );

    setSessions(
      sessionsSnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }))
    );
  };

  const visibleTeams = useMemo(() => {
    if (isAdmin) return teams;
    if (!currentAppUser) return [];

    return teams.filter((team) =>
      Array.isArray(team.mentorIds)
        ? team.mentorIds.includes(currentAppUser.id)
        : false
    );
  }, [teams, isAdmin, currentAppUser]);

  const filteredTeams = useMemo(() => {
    return visibleTeams.filter(
      (team) => team.academicYear === form.academicYear
    );
  }, [visibleTeams, form.academicYear]);

  const visibleSessions = useMemo(() => {
    if (isAdmin) return sessions;
    if (!currentAppUser) return [];

    return sessions.filter((session) =>
      Array.isArray(session.mentorIds)
        ? session.mentorIds.includes(currentAppUser.id)
        : false
    );
  }, [sessions, isAdmin, currentAppUser]);

  const selectedTeam = useMemo(() => {
    return teams.find((team) => team.id === form.teamId) || null;
  }, [teams, form.teamId]);

  const selectedSession = useMemo(() => {
    return sessions.find((session) => session.id === selectedSessionId) || null;
  }, [sessions, selectedSessionId]);

  const selectedTeamMembers = selectedTeam ? getTeamMembers(selectedTeam) : [];
  const selectedTeamMentors = selectedTeam ? getTeamMentors(selectedTeam) : [];

  const selectedSessionMentors = useMemo(() => {
    return users.filter((user) => form.mentorIds.includes(user.id));
  }, [users, form.mentorIds]);

  const isFormValid =
    form.title.trim() &&
    form.academicYear &&
    form.teamId &&
    form.mentorIds.length > 0 &&
    form.date &&
    form.startTime &&
    form.endTime &&
    form.startTime < form.endTime &&
    selectedTeam;

  function getUsersByIds(ids = []) {
    return users.filter((user) => ids.includes(user.id));
  }

  function getTeamMentors(team) {
    return getUsersByIds(Array.isArray(team?.mentorIds) ? team.mentorIds : []);
  }

  function getTeamMembers(team) {
    return getUsersByIds(Array.isArray(team?.memberIds) ? team.memberIds : []);
  }

  const closeModal = () => {
    setModal({
      open: false,
      type: "",
      title: "",
      text: "",
    });
  };

  const resetForm = () => {
    setSelectedSessionId(null);

    setForm({
      title: "Mentör Görüşmesi",
      academicYear: academicYears[0],
      teamId: "",
      mentorIds: [],
      date: "",
      startTime: "",
      endTime: "",
      location: "",
      description: "",
      mentorNotes: "",
      attendance: {},
      status: "active",
    });

    setMessage({ type: "", text: "" });
  };

  const handleSelectSession = (session) => {
    setSelectedSessionId(session.id);

    setForm({
      title: session.title || "Mentör Görüşmesi",
      academicYear: session.academicYear || academicYears[0],
      teamId: session.teamId || "",
      mentorIds: Array.isArray(session.mentorIds) ? session.mentorIds : [],
      date: session.date || "",
      startTime: session.startTime || "",
      endTime: session.endTime || "",
      location: session.location || "",
      description: session.description || "",
      mentorNotes: session.mentorNotes || "",
      attendance: session.attendance || {},
      status: session.status || "active",
    });

    setMessage({ type: "", text: "" });
  };

  const handleAddSessionMentor = (mentorId) => {
    if (!mentorId) return;

    setForm((prev) => ({
      ...prev,
      mentorIds: prev.mentorIds.includes(mentorId)
        ? prev.mentorIds
        : [...prev.mentorIds, mentorId],
    }));
  };

  const handleRemoveSessionMentor = (mentorId) => {
    setForm((prev) => ({
      ...prev,
      mentorIds: prev.mentorIds.filter((id) => id !== mentorId),
    }));
  };

  const handleAttendanceChange = (memberId, field, value) => {
    setForm((prev) => ({
      ...prev,
      attendance: {
        ...prev.attendance,
        [memberId]: {
          ...(prev.attendance?.[memberId] || {}),
          [field]: value,
        },
      },
    }));
  };

  const buildAttendancePayload = () => {
    const authUser = getAuth().currentUser;
    const attendancePayload = {};

    Object.entries(form.attendance || {}).forEach(([memberId, value]) => {
      if (!value?.status && !value?.note) return;

      attendancePayload[memberId] = {
        status: value.status || "",
        note: value.note || "",
        markedBy: authUser?.uid || "",
        markedAt: new Date().toISOString(),
      };
    });

    return attendancePayload;
  };

  const buildSessionPayload = () => {
    const team = selectedTeam;
    const mentorUsers = getUsersByIds(form.mentorIds);
    const memberUsers = getTeamMembers(team);

    return {
      title: form.title.trim(),
      academicYear: form.academicYear,

      teamId: team.id,
      teamName: team.name || "",

      mentorIds: form.mentorIds,
      mentorEmails: mentorUsers.map((user) => user.email).filter(Boolean),

      memberIds: Array.isArray(team.memberIds) ? team.memberIds : [],
      memberEmails: memberUsers.map((user) => user.email).filter(Boolean),

      date: form.date,
      startTime: form.startTime,
      endTime: form.endTime,

      location: form.location.trim(),
      description: form.description.trim(),
      mentorNotes: form.mentorNotes.trim(),
      attendance: buildAttendancePayload(),
      attendanceUpdatedAt: serverTimestamp(),
      status: form.status || "active",

      updatedAt: serverTimestamp(),
    };
  };

  const createSession = async () => {
    if (!isFormValid) return;

    try {
      const payload = buildSessionPayload();

      await addDoc(collection(db, "mentorSessions"), {
        ...payload,
        calendarEventId: "",
        calendarHtmlLink: "",
        calendarStatus: "pending",
        createdBy: getAuth().currentUser.uid,
        createdAt: serverTimestamp(),
      });

      await fetchData();
      resetForm();

      setModal({
        open: true,
        type: "info",
        title: "Oturum Oluşturuldu",
        text: "Oturum başarıyla oluşturuldu. Takvim kaydı ve bildirim süreci otomatik olarak çalışacaktır.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: "Oturum kaydedilemedi. Lütfen tekrar deneyin.",
      });
    }
  };

  const updateSession = async () => {
    if (!selectedSessionId || !isFormValid) return;

    try {
      const payload = buildSessionPayload();

      await updateDoc(doc(db, "mentorSessions", selectedSessionId), payload);

      await fetchData();

      setMessage({
        type: "success",
        text: "Oturum, yoklama ve mentör notları başarıyla güncellendi.",
      });

      closeModal();
    } catch (error) {
      setMessage({
        type: "error",
        text: "Oturum güncellenemedi. Lütfen tekrar deneyin.",
      });

      closeModal();
    }
  };

  const deleteSession = async () => {
    if (!selectedSessionId) return;

    try {
      await deleteDoc(doc(db, "mentorSessions", selectedSessionId));

      await fetchData();
      resetForm();

      setMessage({
        type: "success",
        text: "Oturum silindi.",
      });

      closeModal();
    } catch (error) {
      setMessage({
        type: "error",
        text: "Oturum silinemedi. Lütfen tekrar deneyin.",
      });

      closeModal();
    }
  };

  const handleSaveSession = async (e) => {
    e.preventDefault();

    if (!isFormValid) return;

    if (selectedSessionId) {
      setModal({
        open: true,
        type: "update",
        title: "Oturumu Güncelle",
        text: "Bu oturumu güncellemek istediğinizden emin misiniz? Takvim kaydı ve bildirim süreci güncellenecektir.",
      });

      return;
    }

    await createSession();
  };

  const handleDeleteSession = () => {
    if (!selectedSessionId) return;

    setModal({
      open: true,
      type: "delete",
      title: "Oturumu Sil",
      text: "Bu oturumu silmek istediğinizden emin misiniz? Takvim kaydı silinecek ve katılımcılara bildirim gönderilecektir.",
    });
  };

  if (loading || accessAllowed === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!accessAllowed) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#1565C0] hover:underline"
            >
              <ArrowLeft size={18} />
              Admin Panele Dön
            </button>

            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e8f2ff]">
                <CalendarDays className="text-[#1565C0]" size={26} />
              </div>

              <div>
                <h1 className="text-2xl font-semibold text-[#173b7a]">
                  Oturum Yönetimi
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Mentör görüşme oturumlarını oluşturabilir, güncelleyebilir,
                  yoklama ve görüşme notlarını kaydedebilirsiniz.
                </p>
              </div>
            </div>
          </div>

          {message.text && (
            <div
              className={`rounded-xl px-4 py-3 text-sm font-medium ${
                message.type === "success"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <aside className="rounded-2xl bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-[#173b7a]">
                  Oturumlar
                </h2>

                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#0f4f99]"
                >
                  <Plus size={15} />
                  Yeni
                </button>
              </div>

              <div className="space-y-3">
                {visibleSessions.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Henüz oturum oluşturulmadı.
                  </p>
                ) : (
                  visibleSessions.map((session) => (
                    <button
                      key={session.id}
                      type="button"
                      onClick={() => handleSelectSession(session)}
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        selectedSessionId === session.id
                          ? "border-[#1565C0] bg-[#eef5ff]"
                          : "border-gray-100 bg-[#f8fbff] hover:bg-white"
                      }`}
                    >
                      <p className="font-semibold text-[#173b7a]">
                        {session.title}
                      </p>

                      <p className="mt-1 text-xs text-gray-500">
                        {session.teamName || "-"}
                      </p>

                      <p className="mt-2 text-xs text-gray-500">
                        {session.date || "-"} / {session.startTime || "-"} -{" "}
                        {session.endTime || "-"}
                      </p>

                      <p className="mt-2 text-xs font-medium text-[#1565C0]">
                        Takvim: {session.calendarStatus || "pending"}
                      </p>
                    </button>
                  ))
                )}
              </div>
            </aside>

            <section className="rounded-2xl bg-white p-6 shadow-sm lg:col-span-2">
              <div className="mb-6 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold text-[#173b7a]">
                    {selectedSession
                      ? "Oturum Bilgileri"
                      : "Yeni Oturum Oluştur"}
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    Oturum kaydedildiğinde takvim kaydı ve bildirim süreci
                    otomatik çalışır.
                  </p>
                </div>

                {selectedSession && (
                  <div className="flex gap-2">
                    {selectedSession.calendarHtmlLink && (
                      <a
                        href={selectedSession.calendarHtmlLink}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-blue-100 px-3 py-2 text-sm font-medium text-[#1565C0] transition hover:bg-blue-50"
                      >
                        <ExternalLink size={16} />
                        Takvim
                      </a>
                    )}

                    <button
                      type="button"
                      onClick={handleDeleteSession}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-100 px-3 py-2 text-sm font-medium text-red-500 transition hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                      Sil
                    </button>
                  </div>
                )}
              </div>

              <form onSubmit={handleSaveSession} className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Oturum Başlığı
                  </label>

                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Eğitim Öğretim Yılı
                  </label>

                  <select
                    value={form.academicYear}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        academicYear: e.target.value,
                        teamId: "",
                        mentorIds: [],
                        attendance: {},
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  >
                    {academicYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Takım
                  </label>

                  <select
                    value={form.teamId}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        teamId: e.target.value,
                        mentorIds: [],
                        attendance: {},
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  >
                    <option value="">Takım seçin</option>

                    {filteredTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>

                  {filteredTeams.length === 0 && (
                    <p className="mt-2 text-xs text-red-500">
                      Seçilen eğitim öğretim yılında atanmış takım bulunmuyor.
                    </p>
                  )}
                </div>

                {selectedTeam && (
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Oturum Mentörleri
                    </label>

                    <select
                      value=""
                      onChange={(e) => handleAddSessionMentor(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    >
                      <option value="">Oturum mentörü seçin</option>

                      {selectedTeamMentors
                        .filter((mentor) => !form.mentorIds.includes(mentor.id))
                        .map((mentor) => (
                          <option key={mentor.id} value={mentor.id}>
                            {mentor.fullName} ({mentor.email})
                          </option>
                        ))}
                    </select>

                    {selectedSessionMentors.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {selectedSessionMentors.map((mentor) => (
                          <div
                            key={mentor.id}
                            className="flex items-center justify-between rounded-xl border border-gray-100 bg-[#f8fbff] p-3"
                          >
                            <div>
                              <p className="text-sm font-semibold text-[#173b7a]">
                                {mentor.fullName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {mentor.email}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveSessionMentor(mentor.id)
                              }
                              className="rounded-lg p-2 text-gray-400 transition hover:bg-white hover:text-red-500"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedTeamMentors.length === 0 && (
                      <p className="mt-2 text-xs text-red-500">
                        Bu takıma atanmış mentör bulunmuyor.
                      </p>
                    )}
                  </div>
                )}

                {selectedTeam && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-gray-100 bg-[#f8fbff] p-4">
                      <p className="text-sm font-semibold text-[#173b7a]">
                        Takıma Atanmış Tüm Mentörler
                      </p>

                      <div className="mt-3 space-y-2">
                        {selectedTeamMentors.map((mentor) => (
                          <div key={mentor.id}>
                            <p className="text-sm font-medium text-gray-700">
                              {mentor.fullName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {mentor.email}
                            </p>
                          </div>
                        ))}

                        {selectedTeamMentors.length === 0 && (
                          <p className="text-sm text-gray-500">
                            Mentör bulunmuyor.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-100 bg-[#f8fbff] p-4">
                      <p className="text-sm font-semibold text-[#173b7a]">
                        Takım Üyeleri
                      </p>

                      <div className="mt-3 space-y-2">
                        {selectedTeamMembers.map((member) => (
                          <div key={member.id}>
                            <p className="text-sm font-medium text-gray-700">
                              {member.fullName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {member.email}
                            </p>
                          </div>
                        ))}

                        {selectedTeamMembers.length === 0 && (
                          <p className="text-sm text-gray-500">
                            Üye bulunmuyor.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Tarih
                    </label>

                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Başlangıç
                    </label>

                    <input
                      type="time"
                      value={form.startTime}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          startTime: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                      Bitiş
                    </label>

                    <input
                      type="time"
                      value={form.endTime}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          endTime: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Konum / Toplantı Linki
                  </label>

                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        location: e.target.value,
                      }))
                    }
                    placeholder="Google Meet, Zoom veya oda bilgisi"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Açıklama
                  </label>

                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={4}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                {selectedTeam && (
                  <div className="rounded-2xl border border-gray-100 bg-[#f8fbff] p-5">
                    <h3 className="text-lg font-semibold text-[#173b7a]">
                      Yoklama
                    </h3>

                    <div className="mt-4 space-y-4">
                      {selectedTeamMembers.length === 0 ? (
                        <p className="text-sm text-gray-500">
                          Bu takımda üye bulunmuyor.
                        </p>
                      ) : (
                        selectedTeamMembers.map((member) => {
                          const attendance = form.attendance?.[member.id] || {};

                          return (
                            <div
                              key={member.id}
                              className="rounded-xl border border-gray-100 bg-white p-4"
                            >
                              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                <div>
                                  <p className="text-sm font-semibold text-[#173b7a]">
                                    {member.fullName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {member.email}
                                  </p>
                                </div>

                                <select
                                  value={attendance.status || ""}
                                  onChange={(e) =>
                                    handleAttendanceChange(
                                      member.id,
                                      "status",
                                      e.target.value
                                    )
                                  }
                                  className="rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                                >
                                  {attendanceOptions.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>

                                <input
                                  type="text"
                                  value={attendance.note || ""}
                                  onChange={(e) =>
                                    handleAttendanceChange(
                                      member.id,
                                      "note",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Yoklama notu"
                                  className="rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                                />
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Mentör Görüşme Notları
                  </label>

                  <textarea
                    value={form.mentorNotes}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        mentorNotes: e.target.value,
                      }))
                    }
                    rows={6}
                    placeholder="Görüşmede konuşulan konular, öneriler, takip maddeleri..."
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-[#173b7a]">
                    Durum
                  </label>

                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  >
                    <option value="active">Aktif</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </div>

                {!isFormValid && (
                  <p className="text-sm text-gray-500">
                    Oturum kaydetmek için eğitim öğretim yılı, takım, oturum
                    mentörü, tarih, başlangıç ve bitiş saati seçmelisiniz. Bitiş
                    saati başlangıçtan sonra olmalı.
                  </p>
                )}

                <div className="flex justify-end gap-3">
                  {selectedSession && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
                    >
                      <X size={18} />
                      Vazgeç
                    </button>
                  )}

                  <button
                    type="submit"
                    disabled={!isFormValid}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0f4f99] disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    <Save size={18} />
                    {selectedSession ? "Oturumu Güncelle" : "Oturum Oluştur"}
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      </section>

      {modal.open && (
        <ConfirmModal
          modal={modal}
          onClose={closeModal}
          onConfirm={
            modal.type === "update"
              ? updateSession
              : modal.type === "delete"
              ? deleteSession
              : closeModal
          }
        />
      )}

      <InnovationHubFooter />
    </main>
  );
}

function ConfirmModal({ modal, onClose, onConfirm }) {
  const isDelete = modal.type === "delete";
  const isUpdate = modal.type === "update";
  const isInfo = modal.type === "info";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-xl font-semibold text-[#173b7a]">
              {modal.title}
            </h3>

            <p className="mt-3 text-sm leading-6 text-gray-600">
              {modal.text}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          {!isInfo && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              Vazgeç
            </button>
          )}

          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition ${
              isDelete
                ? "bg-red-500 hover:bg-red-600"
                : isUpdate
                ? "bg-[#1565C0] hover:bg-[#0f4f99]"
                : "bg-[#1565C0] hover:bg-[#0f4f99]"
            }`}
          >
            {isDelete ? "Sil" : isUpdate ? "Güncelle" : "Kapat"}
          </button>
        </div>
      </div>
    </div>
  );
}